import DDxInputSelectorBg from "./../../../../assets/images/ddx-input-selector-bg.webp";
import filter from "./../../../../assets/images/appointment-filter.svg";
import { useNavigate, useSearchParams } from "react-router-dom";
import upcoming from "./../../../../assets/images/upcoming.svg";
import canceled from "./../../../../assets/images/canceled.svg";
import completed from "./../../../../assets/images/completed.svg";
import addCircle from "./../../../../assets/images/add-circle.svg";
import userIcon from "./../../../../assets/images/profile-dark.svg";
import calendarIcon from "./../../../../assets/images/calender-dark.svg";
import arrowIcon from "./../../../../assets/images/shaded-arrow.svg";
import { useEffect, useState } from "react";
import {
  fetchAvatars,
  fetchSymptomsCollectorLink,
  getAppointmentsList,
  getReceptionistAgents,
} from "../../service";
import {
  getLocalStorage,
  setLocalStorage,
} from "../../../../utils/localStorage";
import {
  ENABLE_VOICE_MODE,
  FROM,
  JWT_TOKEN,
  LangSpecificMsg,
  LANGUAGE,
  languageOptions,
  MOBILE_NUMBER,
} from "../../../symptomCollector/utils/constants";
import HeaderAppointment from "../headerAppointment/HeaderAppointment";
import { getDecodedSCToken } from "../../../symptomCollector/symptomCollectorHelper";
import { ValidLanguages } from "../../../symptomCollector/utils/types";
import LoginPopup from "../../../symptomCollector/components/loginPopup/LoginPopup";
import AppointmentDetails from "../appointmentDetails/AppointmentDetails";
import { calculateAge, trackEvent } from "../../../../utils/helper";
import FilterDrawer from "./FilterDrawer";
import dayjs from "dayjs";
import { Spin } from "antd";
import { fetchSCSummary } from "../../../symptomCollector/service";
import LoginFullPage from "../../../symptomCollector/components/loginPopup/LoginFullPage";
import SelectLanguageDrawer from "../selectLanguage/SelectLanguageDrawer";

// Appointment status enum
export enum AppointmentStatus {
  QUEUE = "0",
  ARRIVED = "1",
  ENGAGED = "2",
  FINISHED = "3",
  CANCEL = "4",
}

// Helper function to format date
const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (date.toDateString() === today.toDateString()) {
    return "Today";
  } else if (date.toDateString() === yesterday.toDateString()) {
    return "Yesterday";
  } else {
    return date.toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
      year: "2-digit",
    });
  }
};

// Helper function to format time
const formatTime = (timeString: string) => {
  const [hours, minutes] = timeString.split(":");
  const hour = parseInt(hours);
  const ampm = hour >= 12 ? "PM" : "AM";
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${minutes} ${ampm}`;
};

// Helper function to get status display info
const getStatusInfo = (status: string) => {
  switch (status) {
    case AppointmentStatus.QUEUE:
      return { text: "Queue", icon: upcoming, alt: "queue" };
    case AppointmentStatus.ARRIVED:
      return { text: "Arrived", icon: upcoming, alt: "arrived" };
    case AppointmentStatus.ENGAGED:
      return { text: "Engaged", icon: upcoming, alt: "engaged" };
    case AppointmentStatus.FINISHED:
      return { text: "Finished", icon: completed, alt: "finished" };
    case AppointmentStatus.CANCEL:
      return { text: "Cancelled", icon: canceled, alt: "cancelled" };
    default:
      return { text: "Unknown", icon: upcoming, alt: "unknown" };
  }
};

const HomeAppointment = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const jwtToken = new URLSearchParams(searchParams).get(JWT_TOKEN) || "";
  const decodedToken: any = getDecodedSCToken(jwtToken);
  const [agentDetails, setAgentDetails] = useState<any>({});
  const [filteredAppointments, setFilteredAppointments] = useState<any[]>([]);
  const [appointments, setAppointments] = useState<any>(null);
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const [appliedFilters, setAppliedFilters] = useState<string[]>([]);
  const [phoneNumber, setPhoneNumber] = useState(
    getLocalStorage(MOBILE_NUMBER) || ""
  );
  const [isSCLoggedIn, setIsSCLoggedIn] = useState(
    getLocalStorage(MOBILE_NUMBER)
  );
  const [selectedLanguage, setSelectedLanguage] = useState<string | null>(
    getLocalStorage(LANGUAGE) || null
  );
  const [showAppointmentDetails, setShowAppointmentDetails] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<any>(null);
  const [avatarImage, setAvatarImage] = useState<string>("");
  const LangSpecificMsgData =
    LangSpecificMsg[(selectedLanguage as ValidLanguages) || "english"];
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setLocalStorage(JWT_TOKEN, jwtToken);
  }, []);

  useEffect(() => {
    const hasTrackedLanding = sessionStorage.getItem("hasTrackedLanding");

    if (!hasTrackedLanding) {
      trackEvent("AA_Patient_Landing", {
        "Login Mobile No": phoneNumber,
        "Clinic Id": decodedToken?.user?.hm_id,
        "Clinic Name": decodedToken?.user?.hospital_name,
        "Time Stamp": dayjs().format("YYYY-MM-DD HH:mm:ss"),
        "UTM Source": decodedToken?.user?.utmSource,
      });
      sessionStorage.setItem("hasTrackedLanding", "true");
    }
  }, [
    phoneNumber,
    decodedToken?.user?.hm_id,
    decodedToken?.user?.hospital_name,
  ]);

  useEffect(() => {
    if (appointments?.length === 0 && avatarImage) {
      navigate(`/home-appointment?jwtToken=${jwtToken}`, {
        state: {
          agentDetails: {
            ...agentDetails,
            patient_contact_no: phoneNumber,
          },
          avatarImage: avatarImage,
          isBackButtonVisible: false,
        },
      });
    }
  }, [appointments, avatarImage]);

  useEffect(() => {
    const fetchAppointments = async () => {
      setIsLoading(true);
      let appointments = await getAppointmentsList(
        decodedToken?.user?.hm_id,
        phoneNumber
      );
      appointments = appointments?.map((appointment: any) => {
        if (
          appointment?.pam_status === AppointmentStatus?.QUEUE &&
          dayjs(appointment?.appointment_date).isBefore(dayjs(), "day")
        ) {
          return {
            ...appointment,
            pam_status: AppointmentStatus.CANCEL,
          };
        } else {
          return appointment;
        }
      });
      setAppointments(appointments);
      setFilteredAppointments(appointments);
      setIsLoading(false);
    };
    const fetchReceptionistAgents = async () => {
      const receptionistAgentDetailsRes = await getReceptionistAgents(
        decodedToken?.user?.hm_id
      );
      const decodedTokenRes: any = getDecodedSCToken(
        receptionistAgentDetailsRes
      );
      if (decodedTokenRes?.user) {
        setAgentDetails(decodedTokenRes?.user);
        const res = await fetchAvatars();
        const avatar = res?.find(
          (item: any) => item?._id === decodedTokenRes?.user?.agent_id
        );
        setAvatarImage(avatar?.lookUrl);
      }
    };
    if (isSCLoggedIn) {
      fetchAppointments();
    }
    if (agentDetails && Object.keys(agentDetails).length === 0) {
      fetchReceptionistAgents();
    }
  }, [isSCLoggedIn, agentDetails]);

  // Apply filters when appliedFilters changes
  useEffect(() => {
    if (appliedFilters.length > 0) {
      const filteredAppointments = appointments?.filter((appointment: any) => {
        const status = String(appointment.pam_status);
        const isIncluded = appliedFilters.includes(status);
        return isIncluded;
      });
      setFilteredAppointments(filteredAppointments);
    } else {
      setFilteredAppointments(appointments);
    }
  }, [appliedFilters, appointments]);

  const handleApplyFilter = (filters: any) => {
    setAppliedFilters(filters);
    setShowFilterDrawer(false);
  };

  const handleClearFilters = () => {
    setAppliedFilters([]);
    setSelectedFilters([]);
  };

  const handleViewDetails = (appointment: any) => {
    setSelectedAppointment(appointment);
    setShowAppointmentDetails(true);
  };

  const closeAppointmentDetails = () => {
    setShowAppointmentDetails(false);
    setSelectedAppointment(null);
  };

  const handleSymptomsCollectorLink = async (appointment: any) => {
    const symptomsCollectorLink = await fetchSymptomsCollectorLink({
      um_id: appointment.um_id,
      patient_unique_id: appointment.patient_unique_id,
      patient_contact_no: appointment.patient_contact_no,
      pam_id: appointment.pam_id,
      clinic_id: appointment.clinic_id,
      hm_id: appointment.hm_id,
      hospital_name: appointment.hospital_name,
      hospital_business_id: appointment.hospital_business_id,
      patient_name: appointment.patient_name,
      doctor_name: appointment.doctor_name,
    });

    window.location.href = symptomsCollectorLink;
    sessionStorage.setItem(FROM, "home-appointment");
  };

  return (
    <>
      {!isSCLoggedIn && !(window as any)?.ReactNativeWebView ? (
        <LoginFullPage
          phoneNumber={phoneNumber}
          setPhoneNumber={setPhoneNumber}
          isSCLoggedIn={isSCLoggedIn}
          setIsSCLoggedIn={setIsSCLoggedIn}
          agentDetails={agentDetails}
          setSelectedLanguage={setSelectedLanguage}
        />
      ) : (
        <div className="App symptom-collector relative min-h-screen">
          {/* Background Image */}
          <div className="fixed inset-0">
            <img
              src={DDxInputSelectorBg}
              alt="bg"
              className="h-full w-full object-cover opacity-5"
            />
          </div>

          <HeaderAppointment
            agentDetails={agentDetails}
            setSelectedLanguage={setSelectedLanguage}
            phoneNumber={phoneNumber}
            isSCLoggedIn={isSCLoggedIn}
          />

          {/* Main Content */}
          <div className="relative z-10 p-[18px] w-full">
            {selectedLanguage ? (
              <div className="flex items-center gap-3">
                <p className="text-[#1A1C1E] flex-1 font-semibold text-2xl">
                  Your Appointments
                </p>
                <div className="relative">
                  <img
                    src={filter}
                    alt="filter"
                    onClick={() => {
                      setSelectedFilters(appliedFilters);
                      setShowFilterDrawer(true);
                    }}
                    className="cursor-pointer"
                  />
                  {appliedFilters.length > 0 && (
                    <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center font-medium">
                      {appliedFilters.length}
                    </div>
                  )}
                </div>
              </div>
            ) : null}

            {/* Appointments List */}
            <div className="mt-4 space-y-4 pb-20">
              {selectedLanguage ? (
                isLoading ? (
                  <div className="flex justify-center items-center h-[60vh] mt-auto">
                    <Spin size="large" />
                  </div>
                ) : (
                  filteredAppointments?.map((appointment, index) => {
                    const statusInfo = getStatusInfo(appointment.pam_status);
                    const moengageData = {
                      "Login Mobile No": phoneNumber,
                      "Clinic Id": decodedToken?.user?.hm_id,
                      "Clinic Name": decodedToken?.user?.hospital_name,
                      "Time Stamp": dayjs().format("YYYY-MM-DD HH:mm:ss"),
                      "Owner Doctor Name": appointment.doctor_name,
                      "Owner Doctor Unique ID": appointment.um_id,
                      "Patient Name": appointment.patient_name,
                      "Patient Id": appointment.pm_pid,
                      "Patient Age": calculateAge(appointment.pm_dob)?.years,
                      "Patient Gender": appointment.pm_gender,
                    };

                    return (
                      <div
                        key={index}
                        className="bg-white rounded-2xl shadow-sm"
                      >
                        <div className="relative">
                          {/* Status Badge */}
                          <img
                            src={statusInfo.icon}
                            alt={statusInfo.alt}
                            className="absolute left-[-4px] top-[-1px]"
                          />

                          <div className="p-4 pt-6">
                            {/* Patient Info */}
                            <div className="flex items-center gap-3 mb-3 mt-3">
                              <img src={userIcon} alt="user" />
                              <span className="text-[#1A1A1A] font-semibold">
                                {appointment.pm_first_name}{" "}
                                {appointment.pm_last_name || ""} (
                                {appointment.pm_gender},{" "}
                                {calculateAge(appointment.pm_dob)?.years}yrs)
                              </span>
                            </div>

                            {/* Appointment Time */}
                            <div className="flex items-center gap-3 mb-2">
                              <img src={calendarIcon} alt="calendar" />
                              <div>
                                <div className="text-[#1A1A1A] font-semibold">
                                  {formatDate(appointment.appointment_date)} |{" "}
                                  {formatTime(
                                    appointment.appointment_start_time,
                                  )}
                                </div>
                                <div className="text-[#666666] text-xs">
                                  With {appointment.um_name}
                                </div>
                              </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex gap-2 mt-4">
                              {appointment.pam_status !==
                                AppointmentStatus.FINISHED && (
                                <>
                                  <button
                                    className="flex-1 text-[#4B4AD5] text-sm font-medium"
                                    onClick={() => {
                                      trackEvent(
                                        "AA_View_Details",
                                        moengageData,
                                      );
                                      handleViewDetails(appointment);
                                    }}
                                  >
                                    <span className="font-medium">
                                      View Details
                                    </span>
                                  </button>
                                  <div className="w-[1px] bg-[#E5E5E5]" />
                                </>
                              )}
                              <button
                                className={`flex-1 text-[#4B4AD5] text-sm font-medium flex items-center justify-center 
                            ${
                              (appointment.pam_status ===
                                AppointmentStatus.CANCEL &&
                                appointment.symptomsCollected &&
                                Object.keys(appointment.symptomsCollected)
                                  .length === 0) ||
                              (appointment.pam_status !==
                                AppointmentStatus.QUEUE &&
                                appointment.pam_status !==
                                  AppointmentStatus.FINISHED &&
                                appointment.symptomsCollected &&
                                Object.keys(appointment.symptomsCollected)
                                  .length === 0)
                                ? "opacity-50"
                                : ""
                            }
                            `}
                                onClick={async () => {
                                  if (
                                    appointment.pam_status ===
                                    AppointmentStatus.FINISHED
                                  ) {
                                    trackEvent("AA_View_Rx", moengageData);
                                    navigate("/prescription-details", {
                                      state: {
                                        appointmentData: appointment,
                                      },
                                    });
                                  } else {
                                    if (
                                      (appointment.pam_status ===
                                        AppointmentStatus.FINISHED ||
                                        appointment.pam_status ===
                                          AppointmentStatus.CANCEL ||
                                        appointment.pam_status ===
                                          AppointmentStatus.QUEUE) &&
                                      appointment.symptomsCollected &&
                                      Object.keys(appointment.symptomsCollected)
                                        .length > 0
                                    ) {
                                      try {
                                        const summaryResponse =
                                          await fetchSCSummary(
                                            {
                                              um_id: appointment.um_id,
                                              pam_id: appointment.pam_id,
                                              hm_id: appointment.hm_id,
                                              patient_unique_id:
                                                appointment.patient_unique_id,
                                            },
                                            jwtToken,
                                          );

                                        navigate(
                                          `/symptoms-collector-summary?jwtToken=${jwtToken}`,
                                          {
                                            state: {
                                              summaryResponse: summaryResponse,
                                              id: summaryResponse?._id,
                                              conversation:
                                                summaryResponse?.conversation,
                                              selectedLang: selectedLanguage,
                                              from: "home-appointment",
                                              isAppointmentInQueue:
                                                appointment.pam_status ===
                                                AppointmentStatus.QUEUE,
                                              avatarImage: avatarImage,
                                            },
                                          },
                                        );
                                        trackEvent(
                                          "SC_Patient_Viewed_History",
                                          {
                                            ...moengageData,
                                            doctor_speciality:
                                              decodedToken?.user?.dp_name,
                                            dp_id: decodedToken?.user?.dp_id,
                                          },
                                        );
                                      } catch (error) {
                                        console.error(
                                          "Error fetching conversation ID:",
                                          error,
                                        );
                                      }
                                    } else {
                                      handleSymptomsCollectorLink(appointment);
                                      trackEvent(
                                        "AA_AddSym_Home",
                                        moengageData,
                                      );
                                    }
                                  }
                                }}
                                disabled={
                                  appointment.pam_status ===
                                    AppointmentStatus.CANCEL &&
                                  appointment.symptomsCollected &&
                                  Object.keys(appointment.symptomsCollected)
                                    .length === 0 &&
                                  appointment.pam_status !==
                                    AppointmentStatus.QUEUE &&
                                  appointment.symptomsCollected &&
                                  Object.keys(appointment.symptomsCollected)
                                    .length === 0
                                }
                              >
                                {appointment.pam_status ===
                                  AppointmentStatus.QUEUE &&
                                  appointment.symptomsCollected &&
                                  Object.keys(appointment.symptomsCollected)
                                    .length === 0 && (
                                    <img
                                      src={addCircle}
                                      alt="add-circle"
                                      className="mr-1"
                                    />
                                  )}
                                <span className="font-medium">
                                  {appointment.pam_status ===
                                  AppointmentStatus.FINISHED
                                    ? "View Prescription"
                                    : appointment.symptomsCollected &&
                                        Object.keys(
                                          appointment.symptomsCollected,
                                        ).length > 0
                                      ? "View Symptoms"
                                      : "Add Symptoms"}
                                </span>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )
              ) : null}
            </div>
          </div>

          {selectedLanguage && (
            <div className="fixed bottom-0 w-full z-20 bg-white pt-4 max-w-[450px] mx-auto">
              <div className="mx-4 mb-4">
                <button
                  className="w-full bg-[#4B4AD5] text-white py-4 rounded-2xl text-base font-medium"
                  onClick={() => {
                    trackEvent("AA_BookNewApt", {
                      "Login Mobile No": phoneNumber,
                      "Clinic Id": decodedToken?.user?.hm_id,
                      "Clinic Name": decodedToken?.user?.hospital_name,
                      "Time Stamp": dayjs().format("YYYY-MM-DD HH:mm:ss"),
                    });
                    navigate(
                      `/${
                        ENABLE_VOICE_MODE
                          ? "home-appointment"
                          : "book-appointment"
                      }?jwtToken=${jwtToken}`,
                      {
                        state: {
                          agentDetails: {
                            ...agentDetails,
                            patient_contact_no: phoneNumber,
                          },
                          avatarImage: avatarImage,
                        },
                      },
                    );
                  }}
                >
                  Book New Appointment
                </button>
              </div>
            </div>
          )}

          {showAppointmentDetails && (
            <AppointmentDetails
              show={showAppointmentDetails}
              closePopup={closeAppointmentDetails}
              appointmentData={selectedAppointment}
              agentDetails={agentDetails}
              avatarImage={avatarImage}
            />
          )}

          {showFilterDrawer && (
            <FilterDrawer
              closeDrawer={() => setShowFilterDrawer(false)}
              handleApplyFilter={handleApplyFilter}
              selectedFilters={selectedFilters}
              setSelectedFilters={setSelectedFilters}
              handleResetAll={handleClearFilters}
            />
          )}

          {!selectedLanguage && (
            <SelectLanguageDrawer
              selectedLanguage={selectedLanguage}
              setSelectedLanguage={setSelectedLanguage}
            />
          )}
        </div>
      )}
    </>
  );
};

export default HomeAppointment;
